import React from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {useDispatch, useSelector} from 'react-redux';
import {CSSTransition} from 'react-transition-group';
import styled from 'styled-components';

import {
  selectBrowserNotificationPermStatus,
  selectHideNotificationPermBanner,
  setBrowserNotificationPermStatus,
  setHideNotificationPermBanner,
} from 'app/notifly/slice';
import {gt} from 'app/utils/i18n';
import {WebPushNotifications} from 'app/utils/web-push-notifications';

type Props = {
  wsRegistration: any;
  applicationServerKey: string;
};

export function EnabledDesktopNotificationsBanner({
  wsRegistration,
  applicationServerKey,
}: Props): JSX.Element {
  const isVisible: boolean = useSelector((state) =>
    state.notifications.get('isVisible')
  );
  const browserNotificationPermStatus: NotificationPermission = useSelector(
    selectBrowserNotificationPermStatus
  );
  const isNotificationPermissionBannerHidden: boolean = useSelector(
    selectHideNotificationPermBanner
  );

  const dispatch = useDispatch();

  const hideTemp = () => dispatch(setHideNotificationPermBanner(true));

  React.useEffect(() => {
    subscribeToWebPushNotifications();
    WebPushNotifications.runOnPermissionChange(() => {
      subscribeToWebPushNotifications();

      if (
        WebPushNotifications.canAskForPermission(browserNotificationPermStatus)
      ) {
        dispatch(setHideNotificationPermBanner(false));
      }
    });
  }, []);

  function subscribeToWebPushNotifications() {
    if (
      WebPushNotifications.isSupported() &&
      WebPushNotifications.permissionIsGranted(browserNotificationPermStatus)
    ) {
      WebPushNotifications.subscribeToWebPushMessages(
        wsRegistration,
        applicationServerKey
      );
    }
  }

  function canAskPermission() {
    return (
      WebPushNotifications.isSupported() &&
      WebPushNotifications.canAskForPermission(browserNotificationPermStatus)
    );
  }

  function enableNotifications() {
    WebPushNotifications.requestPermission().then(() => {
      hideTemp();
      dispatch(
        setBrowserNotificationPermStatus(window.Notification.permission)
      );
    });
  }
  const show =
    canAskPermission() &&
    isVisible !== false &&
    !isNotificationPermissionBannerHidden;

  return (
    <CSSTransition
      appear={true}
      in={show}
      timeout={500}
      classNames="banner"
      unmountOnExit
    >
      <StyledTransitionDiv>
        <div className="py-2 px-4 d-flex justify-content-between align-items-center">
          <div>
            <span className="mr-0 mr-md-2">
              <i className="far fa-bell mr-2 d-none d-md-inline" />
              {gt(
                'Be notified by this browser when tasks are due and comments are left'
              )}
            </span>

            <a href="#" className="d-none d-lg-inline" onClick={hideTemp}>
              {gt('Not right now')}
            </a>
            <span className="mx-2 d-none d-lg-inline">&bull;</span>
            <a
              href="#"
              className="d-none d-lg-inline"
              onClick={enableNotifications}
            >
              {gt('Never ask again on this browser')}
            </a>
            <span className="d-none d-md-inline ml-1">
              ({gt('Select block')})
            </span>
            <span className="mx-2 d-none d-lg-inline">&bull;</span>
            <a
              href="#"
              className="d-block d-md-inline py-2 py-md-0 notification-settings"
              onClick={enableNotifications}
            >
              {gt('Allow notifications')}
            </a>
          </div>
          <a href="#" onClick={hideTemp}>
            <i className="fa fa-times" />
          </a>
        </div>
      </StyledTransitionDiv>
    </CSSTransition>
  );
}

const StyledTransitionDiv = styled.div`
  overflow: hidden;

  &.banner-exit {
    max-height: 50px;
  }

  &.banner-exit-active {
    max-height: 1px;
    transition: all 500ms ease-in-out;
  }

  & > div {
    background-color: ${colors.alertBg};
    font-size: 0.9rem;
    color: ${colors.alertText};
    transition: all 250ms;
    max-height: 100px;

    a {
      color: ${colors.alertText};
      font-weight: bold;
    }

    a.notification-settings {
      text-decoration-line: underline;
    }
  }
`;
